import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

import type { PhysicalCardPrintRow } from '../api.js';
import { CardFace } from './physical-card-face.js';

/**
 * Direct printing (D-119, AC §33.9): one CR80 page per row, built in memory and handed to the browser
 * print dialog. The rows live only in this call: the pages get their own React root outside the app,
 * never reach component state, and leave the DOM when the dialog closes, printed or cancelled.
 *
 * The page size is added for the print only, so printing any other admin page is not affected. It
 * is a constructed style sheet because the CSP has no inline styles; the rest is in `card-print.css`.
 */

/** Landscape: the Pebble 4 driver takes landscape pages only, and the card is turned on the page. */
const PAGE_RULE = '@page { size: 85.6mm 53.98mm; margin: 0 }';
const PRINTING_CLASS = 'saay-cr80-printing';
/** The print should not wait forever on a font: the card is readable in the fallback. */
const FONT_WAIT_MS = 3000;

function addPageRule(): () => void {
  if (!('adoptedStyleSheets' in document)) return () => undefined;
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(PAGE_RULE);
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  return () => {
    document.adoptedStyleSheets = document.adoptedStyleSheets.filter((item) => item !== sheet);
  };
}

/** Resolves when the fonts the pages asked for are in, so the first page is not in a fallback. */
async function fontsLoaded(): Promise<void> {
  if (document.fonts === undefined) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    document.fonts.ready,
    new Promise((resolve) => {
      timer = setTimeout(resolve, FONT_WAIT_MS);
    }),
  ]);
  clearTimeout(timer);
}

/** Opens the print dialog for the rows; resolves once the dialog closed and the pages are gone. */
export async function printCardSheets(rows: readonly PhysicalCardPrintRow[]): Promise<void> {
  const host = document.createElement('div');
  host.className = 'saay-cr80-sheet';
  host.setAttribute('aria-hidden', 'true');
  document.body.append(host);
  const root = createRoot(host);
  flushSync(() => {
    root.render(
      rows.map((row, index) => (
        <div className="saay-cr80-page" key={index}>
          <CardFace mode="paper" row={row} />
        </div>
      )),
    );
  });
  const removePageRule = addPageRule();
  document.body.classList.add(PRINTING_CLASS);
  try {
    void host.getBoundingClientRect();
    await fontsLoaded();
    await new Promise<void>((resolve) => {
      window.addEventListener('afterprint', () => resolve(), { once: true });
      window.print();
    });
  } finally {
    document.body.classList.remove(PRINTING_CLASS);
    removePageRule();
    root.unmount();
    host.remove();
  }
}
